import { Fragment } from 'react';
import { HIGHLIGHT_END, HIGHLIGHT_START } from '../types/search';

interface SearchSnippetProps {
  snippet: string | null;
}

/**
 * Render a search snippet, highlighting the runs the backend wrapped in
 * HIGHLIGHT_START/END sentinels. The text is rendered as React text nodes
 * (never dangerouslySetInnerHTML), so transcript content can't inject markup.
 */
export function SearchSnippet({ snippet }: SearchSnippetProps) {
  if (!snippet) {
    return <span className="text-muted italic">(no preview)</span>;
  }

  // snippet shape: plain (START highlighted END plain)*. Splitting on START
  // gives a leading plain run then one "highlighted END plain" chunk each; an
  // unterminated START (no END) is rendered as plain. Sentinels are private-use
  // chars that never occur in document text, so splitting is unambiguous.
  const [head, ...tails] = snippet.split(HIGHLIGHT_START);
  const parts: { text: string; highlight: boolean }[] = [
    { text: head, highlight: false },
  ];
  for (const tail of tails) {
    const end = tail.indexOf(HIGHLIGHT_END);
    if (end === -1) {
      parts.push({ text: tail, highlight: false });
      continue;
    }
    parts.push({ text: tail.slice(0, end), highlight: true });
    parts.push({
      text: tail.slice(end + HIGHLIGHT_END.length),
      highlight: false,
    });
  }

  return (
    <span className="whitespace-pre-wrap break-words text-sm text-ink-2">
      {parts.map((part, i) =>
        part.highlight ? (
          <mark
            key={i}
            className="rounded-sm bg-warn-tint px-0.5 font-medium text-ink"
          >
            {part.text}
          </mark>
        ) : (
          <Fragment key={i}>{part.text}</Fragment>
        )
      )}
    </span>
  );
}
